/* eslint-disable jsx-a11y/anchor-is-valid */

import EventNoteIcon from '@mui/icons-material/EventNote';
import Info from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import React from 'react';

import LocalOffer from '@mui/icons-material/LocalOffer';
import LocationOn from '@mui/icons-material/LocationOn';
import { Typography } from '@mui/material';
import { TwitterPicker } from 'react-color';
import { useSelector } from 'react-redux';
import { primary } from '../../../../../utils/brandColors';
import colors from '../../../../../utils/colors';
import { getBarnUsersForAssignmentString } from '../../../../../utils/scheduleHelper';
import TagsAutocomplete from '../../../../Autocompletes/Tags';
import TaskNameAutocomplete from '../../../../Autocompletes/TaskName';
import TagList from '../../../../TagList';
import ParticipantListComponent from '../../../ParticipantList';

const HeaderSection = ({
  schedule,
  event,
  name,
  setName,
  description,
  setDescription,
  additionalTags,
  setAdditionalTags,
  setDetailedLocation,
  detailedLocation,
  metadata,
  setMetadata,
  disabledForBeingInPast,
  assignment,
  type,
  taskTypeId,
}) => {
  const [displayColorPicker, setDisplayColorPicker] = React.useState(false);
  const [addDescription, setAddDescription] = React.useState(false);
  const [addLocation, setAddLocation] = React.useState(false);
  const [changeTags, setChangeTags] = React.useState(false);

  const barnUsersMap = useSelector((state) => state.barn.barnUsersMap);

  React.useEffect(() => {
    setChangeTags(false);
    if (description && description.length > 0) {
      setAddDescription(true);
    } else {
      setAddDescription(false);
    }
    if (detailedLocation && detailedLocation.length > 0) {
      setAddLocation(true);
    } else {
      setAddLocation(false);
    }
  }, [name]);

  const listItemSx = {
    px: 1,
    py: 0.75,
  };
  const listItemIconSx = {
    minWidth: 32,
    mr: 0.75,
    '& .MuiSvgIcon-root': {
      fontSize: '1.25rem',
      width: '1.25rem',
      height: '1.25rem',
    },
  };

  return (
    <Stack spacing={2} sx={{ m: 1 }}>
      <Paper
        sx={{ borderRadius: 3 }}
      >
        <List disablePadding>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><EventNoteIcon /></ListItemIcon>
            <ListItemText>
              <TaskNameAutocomplete
                sx={{ minWidth: '100%' }}
                size="small"
                variant="outlined"
                id="Name"
                label="Display Name"
                value={name}
                taskTypeId={taskTypeId}
                onChange={(e) => {
                  setName(e.target.value || e.target.textContent || '');
                }}
              />
            </ListItemText>
            {!disabledForBeingInPast && (
              <Box
                sx={{
                  display: 'flex',
                  cursor: 'pointer',
                  alignItems: 'center',
                }}
                onClick={() => { if (!displayColorPicker) { setDisplayColorPicker(true) } }}
                role="presentation"
              >
                <Tooltip title="Change task color">
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: (metadata && metadata.calendarDisplayColor) || primary.main,
                      backgroundColor: (metadata && metadata.calendarDisplayColor) || primary.main,
                      ml: 1,
                    }}
                  />
                </Tooltip>
                {displayColorPicker && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 24,
                      top: 0,
                      zIndex: 100000,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'fixed',
                        inset: 0,
                      }}
                      onClick={() => { setDisplayColorPicker(false); }}
                      role="presentation"
                    />
                    <Paper>
                      <TwitterPicker
                        colors={colors}
                        color={(metadata && metadata.calendarDisplayColor) || primary.main}
                        disabled={disabledForBeingInPast}
                        onChange={(e) => {
                          const calendarDisplayColor = e.hex;
                          setMetadata({
                            ...metadata,
                            calendarDisplayColor,
                          });
                          if (e.hex.length === 7) {
                            setDisplayColorPicker(false);
                          }
                        }}
                        direction="vertical"
                      />
                      <Box
                        sx={{
                          mx: 2,
                          pb: 1,
                          fontSize: 14,
                          fontWeight: 300,
                          zIndex: 10000,
                          position: 'relative',
                        }}
                      >
                        <Typography fontWeight={300} component="div">Pick your own color on the</Typography>
                        <a href="https://g.co/kgs/rxPsQ5R" target="blank" rel="noreferrer">Google Hex Color Picker</a>
                      </Box>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </ListItem>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><LocalOffer /></ListItemIcon>
            <ListItemText>
              {changeTags
                ? (
                  <TagsAutocomplete
                    onSelect={(e, value) => { setAdditionalTags(value); }}
                    value={additionalTags}
                    size="small"
                  />
                )
                : (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <TagList tags={additionalTags} disableGutters />
                    <Link
                      component="button"
                      variant="body2"
                      color="secondary"
                      underline="hover"
                      onClick={() => setChangeTags(true)}
                    >
                      Modify Tags
                    </Link>
                  </Stack>
                )}
            </ListItemText>
          </ListItem>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><Info /></ListItemIcon>
            <ListItemText>
              {addDescription
                ? (
                  <TextField
                    sx={{ minWidth: '100%' }}
                    size="small"
                    variant="outlined"
                    id="description"
                    label="Information"
                    value={description}
                    multiline
                    rows="2"
                    onChange={(e) => setDescription(e.target.value)}
                  />
                )
                : (
                  <Link
                    component="button"
                    variant="body2"
                    color="secondary"
                    underline="hover"
                    onClick={() => setAddDescription(true)}
                  >
                    Add Task Information
                  </Link>
                )}
            </ListItemText>
          </ListItem>
          <ListItem dense sx={listItemSx}>
            <ListItemIcon sx={listItemIconSx}><LocationOn /></ListItemIcon>
            <ListItemText>
              {addLocation
                ? (
                  <TextField
                    sx={{
                      minWidth: '100%',
                      textWrap: 'auto',
                    }}
                    size="small"
                    variant="outlined"
                    id="detailed-location"
                    label="Location Details"
                    value={detailedLocation || ''}
                    onChange={(e) => setDetailedLocation(e.target.value)}
                    disabled={disabledForBeingInPast}
                    helperText={disabledForBeingInPast && 'Cannot be modified after the schedule start time of the task'}
                  />
                )
                : (
                  <Link
                    component="button"
                    variant="body2"
                    color="secondary"
                    underline="hover"
                    onClick={() => setAddLocation(true)}
                  >
                    {disabledForBeingInPast
                      ? 'No location details specified'
                      : 'Add Task Location'}
                  </Link>
                )}
            </ListItemText>
          </ListItem>
        </List>
      </Paper>
      {schedule && schedule.id && event && type === 'Invitation' && (
        <ParticipantListComponent
          participants={getBarnUsersForAssignmentString(assignment || '', barnUsersMap)}
          schedule={schedule}
          occurrenceDate={event && event.start}
          defaultOpen={false}
        />
      )}
    </Stack>
  );
};

HeaderSection.propTypes = {
  schedule: PropTypes.object,
  event: PropTypes.object,
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  description: PropTypes.string.isRequired,
  setDescription: PropTypes.func.isRequired,
  additionalTags: PropTypes.array.isRequired,
  setAdditionalTags: PropTypes.func,
  setDetailedLocation: PropTypes.func,
  detailedLocation: PropTypes.string,
  metadata: PropTypes.any,
  setMetadata: PropTypes.func,
  disabledForBeingInPast: PropTypes.bool,
  assignment: PropTypes.string,
  type: PropTypes.string,
  taskTypeId: PropTypes.number,
};

HeaderSection.defaultProps = {
  schedule: null,
  event: null,
  detailedLocation: null,
  disabledForBeingInPast: false,
  assignment: '',
  type: 'Default',
  setAdditionalTags: () => { },
  setDetailedLocation: () => { },
  metadata: {},
  setMetadata: () => { },
  taskTypeId: null,
};

export default HeaderSection;
